import { useLazyFileLoad } from "@/shared/hooks";
import React, { useRef, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Pause, Play } from "react-bootstrap-icons";
import WaveSurfer from "wavesurfer.js";

const formatBytes = (bytes: number, decimals = 1): string => {
  if (bytes === 0) return "0 Б";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Б", "КБ", "МБ", "ГБ"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

interface WaveSurferPlayerProps {
  src: string | File;
  isUser: boolean;
  isSelected?: boolean;
}

export const WaveSurferPlayer: React.FC<WaveSurferPlayerProps> = ({
  src,
  isUser,
  isSelected = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [fileSizeDisplay, setFileSizeDisplay] = useState<string>("—");
  const [error, setError] = useState(false);

  const {
    url: audioUrl,
    size,
    loading: isLoading,
    error: loadError,
  } = useLazyFileLoad(containerRef, src, { threshold: 0.1 });

  useEffect(() => {
    if (size !== null) {
      setFileSizeDisplay(formatBytes(size));
    } else {
      setFileSizeDisplay("—");
    }
  }, [size]);

  useEffect(() => {
    if (loadError) {
      setError(true);
    }
  }, [loadError]);

  // Инициализация WaveSurfer при появлении audioUrl
  useEffect(() => {
    if (!audioUrl || !waveformRef.current) return;

    let cancelled = false;

    const initWaveSurfer = () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }

      const ws = WaveSurfer.create({
        container: waveformRef.current!,
        waveColor: "#ccc",
        progressColor: "#4a90e2",
        height: 20,
        cursorWidth: 0,
        interact: true,
      });

      wavesurferRef.current = ws;

      ws.on("ready", () => {
        if (!cancelled) {
          const duration = ws.getDuration() * 1000;
          setDurationMs(duration);
          setIsReady(true);

          // Определяем размер файла
          if (
            typeof src === "string" &&
            (src.startsWith("http://") || src.startsWith("https://")) &&
            !src.startsWith("blob:")
          ) {
          } else if (src instanceof File) {
            setFileSizeDisplay(formatBytes(src.size));
          } else {
            // Для http/blob — размер неизвестен → скрываем или ставим "—"
            setFileSizeDisplay("—");
          }
        }
      });

      ws.on("play", () => !cancelled && setIsPlaying(true));
      ws.on("pause", () => !cancelled && setIsPlaying(false));
      ws.on("finish", () => !cancelled && setIsPlaying(false));

      ws.load(audioUrl).catch((err) => {
        if (!cancelled && err.name !== "AbortError") {
          console.error("WaveSurfer load error:", err);
          setError(true);
        }
      });

      return () => {
        cancelled = true;
        if (wavesurferRef.current) {
          wavesurferRef.current.destroy();
          wavesurferRef.current = null;
        }
      };
    };

    const cleanup = initWaveSurfer();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [audioUrl]);

  const togglePlay = () => {
    if (isReady && wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const formatTime = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(
      s % 60
    ).padStart(2, "0")}`;
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
      }}
    >
      <Button
        variant={isUser ? "secondary" : "light"}
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          togglePlay();
        }}
        disabled={!isReady || isLoading}
        style={{
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        {isLoading ? (
          <span className="spinner-border spinner-border-sm" />
        ) : isPlaying ? (
          <Pause size={16} color={!isUser ? "#6c757d" : "#fff"} />
        ) : (
          <Play size={16} color={!isUser ? "#6c757d" : "#fff"} />
        )}
      </Button>

      <div
        style={{
          flex: 1,
          minWidth: "150px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          ref={waveformRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            height: "20px",
            cursor: isReady ? "pointer" : "default",
            opacity: isLoading ? 0.6 : 1,
          }}
        />
        <small style={{ fontSize: "0.6rem", color: isSelected ? "#d6d6d6" : isUser ? "#999" : "#d6d6d6", textAlign: "left" }}>
          {isLoading
            ? "Загрузка..."
            : error
              ? "Ошибка загрузки"
              : `${formatTime(durationMs)}${fileSizeDisplay !== "—" ? ` / ${fileSizeDisplay}` : ""
              }`}
        </small>
      </div>
    </div>
  );
};
